// akunkas.repo — akun kas (tunai · rekening bank · QRIS) & buku kas non-tiket.
//
// Kas dari pembayaran tiket SENGAJA tidak disalin ke `cash_moves`: ia sudah dihitung dari
// tiketnya sendiri (domain/kasir). Dua sumber untuk satu uang = cepat atau lambat terhitung dua
// kali. Tabel ini hanya untuk uang yang tak punya tiket: beban, setoran, pencairan QRIS,
// modal/prive, bayar supplier, penyesuaian.
import { supabase } from '@core/supabase';
import { fetchAllPaged } from '@core/paging';
import { currentUser } from '@core/session';
import type { AkunKas, MutasiKas, JenisMutasi } from '@domain/akunkas';

const KOL = 'id,nama,jenis,outlet,bank_nama,no_rek,atas_nama,saldo_awal,tgl_awal,aktif,urut,kode_akun,catatan,kas_kecil';

export interface AkunRow extends AkunKas { bankNama: string | null; noRek: string | null; atasNama: string | null; catatan: string | null;
  /** Saldo laci tetap (imprest) — hanya bermakna untuk akun tunai. */
  kasKecil: number }

const keAkun = (r: Record<string, unknown>): AkunRow => ({
  id: r['id'] as number, nama: r['nama'] as string, jenis: r['jenis'] as AkunKas['jenis'],
  outlet: (r['outlet'] as string) ?? null, saldoAwal: (r['saldo_awal'] as number) ?? 0,
  tglAwal: (r['tgl_awal'] as string) ?? null, aktif: r['aktif'] !== false,
  urut: (r['urut'] as number) ?? 0, kodeAkun: (r['kode_akun'] as string) ?? null,
  bankNama: (r['bank_nama'] as string) ?? null, noRek: (r['no_rek'] as string) ?? null,
  atasNama: (r['atas_nama'] as string) ?? null, catatan: (r['catatan'] as string) ?? null,
  kasKecil: (r['kas_kecil'] as number) ?? 0,
});

export async function listAkun(semua = false): Promise<AkunRow[]> {
  let q = supabase().from('cash_accounts').select(KOL).order('urut').order('id');
  if (!semua) q = q.eq('aktif', true);
  const { data, error } = await q;
  if (error) throw new Error(error.message);
  return (data ?? []).map((r) => keAkun(r as Record<string, unknown>));
}

export type AkunInput = Omit<AkunRow, 'id'>;

export async function simpanAkun(id: number | null, d: AkunInput): Promise<void> {
  const row = {
    nama: d.nama.trim(), jenis: d.jenis, outlet: d.outlet, bank_nama: d.bankNama, no_rek: d.noRek,
    atas_nama: d.atasNama, saldo_awal: d.saldoAwal, tgl_awal: d.tglAwal || null, aktif: d.aktif,
    urut: d.urut, kode_akun: d.kodeAkun || null, catatan: d.catatan, kas_kecil: Math.max(0, d.kasKecil ?? 0),
  };
  const q = supabase().from('cash_accounts');
  const { error } = id ? await q.update(row).eq('id', id) : await q.insert(row);
  if (error) throw new Error(error.message);
}

/** Set saldo awal + tanggal mulai satu akun kas (dipakai dialog Saldo Awal Neraca per-akun). */
export async function setSaldoAwal(id: number, saldoAwal: number, tglAwal: string | null): Promise<void> {
  const { error } = await supabase().from('cash_accounts').update({ saldo_awal: Math.round(saldoAwal), tgl_awal: tglAwal || null }).eq('id', id);
  if (error) throw new Error(error.message);
}

/** Akun tak pernah dihapus bila sudah dipakai — cukup dinonaktifkan, supaya riwayat tetap utuh. */
export async function nonaktifkanAkun(id: number, aktif: boolean): Promise<void> {
  const { error } = await supabase().from('cash_accounts').update({ aktif }).eq('id', id);
  if (error) throw new Error(error.message);
}

const MV = 'id,tgl,akun_id,arah,jumlah,jenis,pasangan_id,ref_tabel,ref_id,catatan,by_user,outlet';
export interface MutasiRow extends MutasiKas { id: number; byUser: string | null; outlet: string | null; refTabel: string | null; refId: string | null }

const keMutasi = (r: Record<string, unknown>): MutasiRow => ({
  id: r['id'] as number, tgl: r['tgl'] as string, akunId: r['akun_id'] as number,
  arah: r['arah'] as 'masuk' | 'keluar', jumlah: (r['jumlah'] as number) ?? 0,
  jenis: r['jenis'] as JenisMutasi, catatan: (r['catatan'] as string) ?? null,
  byUser: (r['by_user'] as string) ?? null, outlet: (r['outlet'] as string) ?? null,
  refTabel: (r['ref_tabel'] as string) ?? null, refId: (r['ref_id'] as string) ?? null,
});

export async function mutasiTanggal(tgl: string, outlet?: string | null): Promise<MutasiRow[]> {
  let q = supabase().from('cash_moves').select(MV).eq('tgl', tgl).order('id');
  if (outlet) q = q.eq('outlet', outlet);
  const { data, error } = await q;
  if (error) throw new Error(error.message);
  return (data ?? []).map((r) => keMutasi(r as Record<string, unknown>));
}

/**
 * Saldo tiap akun SEBELUM tanggal tertentu, dari mutasi non-tiket + saldo awal akun.
 *
 * CATATAN JUJUR: kas dari tiket hari-hari sebelumnya belum ikut di sini — menghitungnya berarti
 * menyapu seluruh riwayat tiket tiap kali layar dibuka. Yang dipakai sebagai titik mulai adalah
 * `saldo_awal` akun (diisi sekali) + hasil tutup kasir hari sebelumnya. Bila Apo kelak ingin
 * saldo berjalan penuh, simpan saldo akhir tiap penutupan lalu baca dari situ.
 */
export async function saldoSebelum(tgl: string, akun: AkunRow[]): Promise<Map<number, number>> {
  // Paging: mutasi kas menumpuk tiap hari → saldo awal Tutup Kasir salah setelah >1.000 baris.
  const data = await fetchAllPaged<{ akun_id: number; arah: string; jumlah: number }>((a, b) =>
    supabase().from('cash_moves').select('akun_id,arah,jumlah').lt('tgl', tgl).order('id').range(a, b));
  const m = new Map<number, number>();
  for (const a of akun) m.set(a.id, a.saldoAwal);
  for (const r of data) {
    const kini = m.get(r.akun_id) ?? 0;
    m.set(r.akun_id, kini + (r.arah === 'masuk' ? r.jumlah : -r.jumlah));
  }
  return m;
}

export async function tambahMutasi(d: {
  tgl: string; akunId: number; arah: 'masuk' | 'keluar'; jumlah: number; jenis: JenisMutasi;
  catatan?: string | null; refTabel?: string | null; refId?: string | null; outlet?: string | null;
}): Promise<number> {
  const u = currentUser();
  const { data, error } = await supabase().from('cash_moves').insert({
    tgl: d.tgl, akun_id: d.akunId, arah: d.arah, jumlah: Math.round(d.jumlah), jenis: d.jenis,
    catatan: d.catatan ?? null, ref_tabel: d.refTabel ?? null, ref_id: d.refId ?? null,
    outlet: d.outlet ?? u?.outlet ?? null, by_user: u?.nama ?? null,
  }).select('id').single();
  if (error) throw new Error(error.message);
  return (data as { id: number }).id;
}

/**
 * Perpindahan antar akun (setoran tunai→bank, pencairan QRIS→bank): DUA baris yang saling
 * menunjuk. Ini BUKAN pengeluaran — kalau dicatat sebagai beban, laba akan tergerus padahal
 * uangnya cuma berpindah tempat.
 */
export async function pindahAkun(d: {
  tgl: string; dariId: number; keId: number; jumlah: number; jenis: 'setoran' | 'pencairan';
  catatan?: string | null; outlet?: string | null;
}): Promise<number> {
  const keluar = await tambahMutasi({ ...d, akunId: d.dariId, arah: 'keluar', jenis: d.jenis });
  const masuk = await tambahMutasi({ ...d, akunId: d.keId, arah: 'masuk', jenis: d.jenis });
  const sb = supabase();
  await sb.from('cash_moves').update({ pasangan_id: masuk }).eq('id', keluar);
  await sb.from('cash_moves').update({ pasangan_id: keluar }).eq('id', masuk);
  return keluar; // dipegang baris setoran → menghapus barisnya ikut membatalkan mutasinya
}

export async function hapusMutasi(id: number): Promise<void> {
  const sb = supabase();
  // Pasangannya ikut dihapus: separuh perpindahan yang tertinggal membuat saldo dua akun salah
  // sekaligus, dan tak ada yang tahu asal selisihnya.
  const { data } = await sb.from('cash_moves').select('pasangan_id').eq('id', id).maybeSingle();
  const pasangan = (data as { pasangan_id: number | null } | null)?.pasangan_id ?? null;
  const { error } = await sb.from('cash_moves').delete().eq('id', id);
  if (error) throw new Error(error.message);
  if (pasangan) await sb.from('cash_moves').delete().eq('id', pasangan);
}

/**
 * Beban hari itu yang BELUM dipilih sumber dananya. Angka ini sering persis sama dengan selisih
 * tutup kasir: uangnya sudah keluar laci, tapi aplikasi tak tahu dari akun mana.
 */
export async function bebanBelumDibayar(tgl: string, outlet?: string | null): Promise<number> {
  let q = supabase().from('expenses').select('jumlah,akun_id').eq('tgl_iso', tgl).is('akun_id', null);
  if (outlet) q = q.eq('outlet', outlet);
  const { data, error } = await q;
  if (error) return 0;                       // kolom belum ada / tak berhak baca → jangan gagalkan layar
  return (data ?? []).reduce((a, r) => a + ((r as { jumlah: number }).jumlah ?? 0), 0);
}
